/**
 * timeSeriesSSOTAdapter.js
 *
 * Phase 5A — Time-Series Adapter. Converts buildCumulativeSeries()'s raw
 * output (an array of full buildCumulativeData()-shaped snapshots) into
 * the canonical TimeSeriesResult shape the Visual Engine consumes.
 *
 * This module does NOT compute anything financial and does NOT modify
 * buildCumulativeSeries() — it is a pure reshape layer, exactly the same
 * role financialSSOTAdapter.js and historicalSSOTAdapter.js already play
 * for their respective Owners.
 *
 *   buildCumulativeSeries()  →  timeSeriesSSOTAdapter  →  TimeSeriesResult  →  Visual Engine
 *
 * Explicitly out of scope for Phase 5A (per approved decision):
 *   - No Visual component reads from this module directly through
 *     anything other than the shape defined here.
 *   - No change to buildCumulativeSeries(), ProjectCurves.jsx, or
 *     Scheduling.jsx.
 */

import { buildCumulativeSeries } from './buildCostControlData'
import { validateQuery } from './analyticalQuery'
import { supabase } from './supabase'

// ─── Multi-select Filter Contract (approved, same as financialSSOTAdapter.js —
// not re-documented in full here to avoid drift; see that file's header) ───
function matchesFilter(rowValue, filterValue) {
  if (filterValue == null || filterValue === 'all') return true
  if (Array.isArray(filterValue)) return filterValue.includes(rowValue)
  return rowValue === filterValue
}
function matchesAnyFilter(candidateValues, filterValue) {
  if (filterValue == null || filterValue === 'all') return true
  return candidateValues.some(v => matchesFilter(v, filterValue))
}

// ─── Project-level measure mapping (off one series point's grandTotals/meta) ─
// Verified against buildCumulativeSeries()'s actual output shape (identical
// to buildCumulativeData()'s) before writing this — not assumed from the
// Phase 1/2 Adapter's list, since a time-series point is a LIVE snapshot
// (via computeSnapshotFromFetchedData()), not a frozen forecast_history
// row like historical_comparison's. contractValue/grossProfit/budgetProfit
// ARE available here — unlike historical_comparison, where they were
// explicitly rejected because forecast_history never froze them.
function mapProjectLevelMeasure(measureKey, point) {
  const { grandTotals, meta } = point
  switch (measureKey) {
    case 'budget':        return grandTotals.bac
    case 'actual':          return grandTotals.actualTotal
    case 'eac':               return grandTotals.eac
    case 'commitment':          return grandTotals.commitment
    case 'variance':              return grandTotals.bac - grandTotals.eac
    case 'cpi':                     return grandTotals.cpi
    // contractValue is a fixed reference value (read once from `projects`
    // by extractFetchedData(), not per-forecastDate) — it will read
    // identically on every point in the series. This is correct, not a
    // bug: the contract itself doesn't change across forecast points, only
    // EAC/Budget do. Documented here so a consumer isn't surprised to see
    // the same number repeated across every series point.
    case 'contractValue':          return meta.contractValue
    case 'grossProfit':              return meta.grossProfit
    case 'budgetProfit':               return meta.budgetProfit
    default:
      throw new Error(`Unmapped time-series measure: "${measureKey}"`)
  }
}

// ─── Row-level (Cost Code grain) measure mapping ───────────────────────────
function mapRowLevelMeasure(measureKey, row) {
  switch (measureKey) {
    case 'budget':      return row.bac || 0
    case 'actual':       return row.totalActualCost || 0
    case 'eac':           return row.eac || 0
    case 'commitment':     return row.commitment || 0
    case 'cpi':             return row.cpi || 0
    case 'variance':         return (row.bac || 0) - (row.eac || 0)
    case 'contractValue':
    case 'grossProfit':
    case 'budgetProfit':
      throw new Error(
        `'${measureKey}' is project-level metadata and has no per-Cost-Code ` +
        `value — query it with dimension: null instead.`
      )
    default:
      throw new Error(`Unmapped time-series measure: "${measureKey}"`)
  }
}

// ─── Cost Code grain rows off a single series point ────────────────────────
// FILTER CONTRACT (Phase 6A, approved) — identical to
// financialSSOTAdapter.js's getDimensionRows(): cost_type/
// indirect_category are direct row-level equality checks;
// resource_type is a descendant-resource EXISTENCE check (membership,
// not measure reallocation — see that file's header comment for the
// full rationale, not repeated here to avoid drift between the two
// copies of the same contract).
function getCostCodeRows(point, filters) {
  let rows = [...(point.directRows || []), ...(point.indirectRows || [])]
  if (filters?.cost_code != null) {
    rows = rows.filter(r => matchesFilter(r.code, filters.cost_code))
  }
  if (filters?.cost_type != null) {
    rows = rows.filter(r => matchesFilter(r.cost_type, filters.cost_type))
  }
  if (filters?.indirect_category != null) {
    rows = rows.filter(r => matchesFilter(r.indirect_category_id, filters.indirect_category))
  }
  if (filters?.resource_type != null) {
    rows = rows.filter(r =>
      matchesAnyFilter((r.resourceRows || []).map(res => res.resource_type), filters.resource_type)
    )
  }
  if (filters?.sub_code != null) {
    rows = rows.filter(r =>
      matchesAnyFilter((r.children || []).map(c => c.code), filters.sub_code)
    )
  }
  if (filters?.resource_code != null) {
    rows = rows.filter(r =>
      matchesAnyFilter((r.resourceRows || []).map(res => res.code), filters.resource_code)
    )
  }
  return rows.map(r => ({ key: r.code, label: r.description || r.code, row: r }))
}

// ─── sub_code grain rows off a single series point (flat, direct read) ─────
// Same posture as financialSSOTAdapter.js's sub_code dimension — each
// child already carries real, independently-computed values, confirmed
// in Stage 1's Final Gate against buildCostControlData.js's subRows
// construction. buildCumulativeSeries() reuses the exact same
// computeSnapshotFromFetchedData() code path per point, so this holds
// identically at every forecastDate.
// Field name correction: subRows' final shape is `{ ...sub, ...enriched }`
// where `sub` comes from cost_codes' own `code`/`description` columns —
// the field is `code`, NOT `rowKey` (that name only exists on a
// different, internal-only object elsewhere in buildCostControlData.js).
// See financialSSOTAdapter.js's matching fix for the full trace.
function getSubCodeRows(point, filters) {
  const costCodeRows = getCostCodeRows(point, filters)
  return costCodeRows.flatMap(({ row }) =>
    (row.children || []).map(c => ({
      key: c.code,
      label: `${c.code} (${row.description || row.code})`,
      row: c,
    }))
  )
}

// ─── resource_code grain rows off a single series point (flat, direct read) ─
function getResourceCodeRows(point, filters) {
  const costCodeRows = getCostCodeRows(point, filters)
  return costCodeRows.flatMap(({ row }) =>
    (row.resourceRows || []).map(res => ({
      key: res.code,
      label: `${res.code} (${row.description || row.code})`,
      row: res,
    }))
  )
}

// ─── resource_type grain rows off a single series point (Direct only) ──────
// Reads THIS point's resourceTypeTotals verbatim — same 4 fixed keys, no
// Unclassified, same posture as financialSSOTAdapter.js's Extension. No
// re-summing across points; each series point's resourceTypeTotals is
// already the pre-aggregated total for that forecastDate.
function getResourceTypeRows(point) {
  return Object.entries(point.resourceTypeTotals).map(([type, totals]) => ({
    key: type, label: type, row: totals,
  }))
}

// ─── indirect_category grain rows off a single series point (Indirect only) ─
function getIndirectCategoryRows(point) {
  const rows = point.indirectCategories.map(cat => ({
    key: cat.name, label: cat.name, row: cat.totals,
  }))
  rows.push({ key: 'Uncategorized', label: 'Uncategorized', row: point.uncategorizedTotals })
  return rows
}

/**
 * Executes an AnalyticalQuery against the time_series dataset, returning
 * the canonical TimeSeriesResult:
 *
 *   {
 *     grain: 'project' | 'cost_code',
 *     series: Array<{
 *       time: string,          // the forecastDate this point represents
 *       hasSnapshot: boolean,  // false when no forecast_history row
 *                               // matched this date (Not-Forecasted Budget
 *                               // fallback was used instead — same
 *                               // fallback buildCumulativeData() itself
 *                               // uses, not an error condition)
 *       rows: Array<{ key, label, values }>
 *     }>
 *   }
 *
 * dimension MUST be 'cost_code', 'resource_type', 'indirect_category', or
 * null. package/sub_code are not yet validated for time-series use.
 *
 * resource_type/indirect_category on EACH series point read the SAME
 * pre-aggregated fields buildCumulativeData() computes on every call —
 * confirmed by inspecting buildCumulativeSeries()'s own return statement
 * (`{ forecastDate, ...computeSnapshotFromFetchedData(...) }`): every
 * point carries the full resourceTypeTotals/indirectCategoriesWithTotals/
 * uncategorizedTotals fields, not a subset. Same values/grain/gaps as
 * financialSSOTAdapter.js's Extension (4 fixed resource_type keys, no
 * Unclassified; indirect_category includes an 'Uncategorized' row) — NOT
 * re-verified as identical to monthly_actual_cost's categorization,
 * which remains a deliberately separate Owner.
 */
export async function executeTimeSeriesQuery(projectId, forecastDates, query) {
  const { valid, errors } = validateQuery(query)
  if (!valid) throw new Error(`Invalid query: ${errors.join('; ')}`)
  if (query.dataset !== 'time_series') {
    throw new Error(
      `Dataset "${query.dataset}" is not handled by timeSeriesSSOTAdapter — ` +
      `only "time_series" is wired up here.`
    )
  }
  const validDimensions = [null, 'cost_code', 'resource_type', 'indirect_category', 'sub_code', 'resource_code']
  if (!validDimensions.includes(query.dimension)) {
    throw new Error(
      `time_series supports dimension: 'cost_code', 'resource_type', ` +
      `'indirect_category', 'sub_code', 'resource_code', or null — ` +
      `"${query.dimension}" is out of scope.`
    )
  }

  const rawSeries = await buildCumulativeSeries(projectId, forecastDates)

  // A point's forecastDate may have no matching forecast_history row at
  // all (the Not-Forecasted Budget fallback documented in Phase 4's Gate
  // Review). That is NOT distinguishable from computed EAC alone — a
  // genuine snapshot whose EAC equals Budget looks identical to "no
  // snapshot existed". hasSnapshot is therefore determined by a direct,
  // lightweight existence check against forecast_history (see
  // checkSnapshotExistence below), not inferred from any computed value.
  const hasSnapshotByDate = await checkSnapshotExistence(projectId, forecastDates)

  const finalSeries = rawSeries.map(point => {
    const dimensionRows = !query.dimension
      ? null // handled separately below (project-level)
      : (query.dimension === 'cost_code') ? getCostCodeRows(point, query.filters)
      : (query.dimension === 'resource_type') ? getResourceTypeRows(point)
      : (query.dimension === 'indirect_category') ? getIndirectCategoryRows(point)
      : (query.dimension === 'sub_code') ? getSubCodeRows(point, query.filters)
      : getResourceCodeRows(point, query.filters)

    const values_or_rows = !query.dimension
      ? [{
          key: projectId,
          label: 'Project',
          values: buildValues(query, (m) => mapProjectLevelMeasure(m, point)),
        }]
      : dimensionRows.map(({ key, label, row }) => ({
          key, label,
          values: buildValues(query, (m) => mapRowLevelMeasure(m, row)),
        }))

    return {
      time: point.forecastDate,
      hasSnapshot: hasSnapshotByDate.has(point.forecastDate),
      rows: values_or_rows,
    }
  })

  return { grain: query.dimension || 'project', series: finalSeries }
}

function buildValues(query, mapFn) {
  const values = {}
  for (const m of query.measures) {
    values[m] = mapFn(m)
  }
  if (query.comparison?.type === 'variance') {
    const baseline = mapFn(query.comparison.baseline)
    for (const m of query.measures) {
      if (m === query.comparison.baseline) continue
      values[`${m}_vs_${query.comparison.baseline}`] = values[m] - baseline
    }
  }
  return values
}

// Lightweight existence check — does NOT duplicate the 22-query fetch,
// just confirms which of the requested forecastDates have at least one
// forecast_history row, so hasSnapshot can be reported accurately without
// guessing from computed EAC values.
async function checkSnapshotExistence(projectId, forecastDates) {
  const { data } = await supabase
    .from('forecast_history')
    .select('forecast_date')
    .eq('project_id', projectId)
    .in('forecast_date', forecastDates)
  const found = new Set((data || []).map(r => r.forecast_date))
  return found
}
